import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import {
    isMapsConfigured,
    fetchAddressSuggestions,
    resolvePlace,
    newSessionToken,
} from '../utils/googleMaps';
import FormField from './FormField';

/**
 * Address field with Google Places (New) autocomplete.
 *
 * Predictions are fetched through the Autocomplete Data API and rendered in our
 * own dropdown, rather than using the legacy `places.Autocomplete` widget —
 * that service is not enabled for projects created after March 2025, and owning
 * the markup lets the same component serve both the wizard form and the compact
 * inline editor on the client details page.
 *
 * Choosing a suggestion calls `onResolve` with everything Google filled in —
 * city, county, postcode, country and the lat/lng pin.
 *
 * Degrades to a plain text input when no API key is set or a request fails, so
 * address entry never depends on Maps being reachable.
 *
 * `variant`: 'form' matches FormField's floating-label style (the wizard);
 *            'inline' matches EditableField's compact grid cell.
 */

// Wait this long after the last keystroke before asking Google. Keeps request
// volume (and therefore cost) sane while still feeling instant.
const DEBOUNCE_MS = 250;

// ISO 3166-1 alpha-2. Note the UK is 'gb' — the new API rejects 'uk'.
const DEFAULT_REGIONS = ['gb'];

const AddressAutocomplete = ({
    label = 'Address Line 1',
    name = 'address_line_1',
    value,
    onChange,
    onResolve,
    placeholder = 'Start typing an address…',
    regionCodes = DEFAULT_REGIONS,
    required = false,
    className = '',
    variant = 'form',
}) => {
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [highlight, setHighlight] = useState(-1);

    const wrapRef = useRef(null);
    const sessionRef = useRef(null);
    const timerRef = useRef(null);
    // Bumped on every keystroke so a slow in-flight response can't overwrite a
    // newer one.
    const requestSeq = useRef(0);
    // Set while we apply a resolved place, so the resulting onChange doesn't
    // immediately re-open the dropdown.
    const applyingRef = useRef(false);

    const regionKey = JSON.stringify(regionCodes);

    const close = useCallback(() => {
        setOpen(false);
        setHighlight(-1);
    }, []);

    // Clicking outside dismisses the list.
    useEffect(() => {
        if (!open) return undefined;
        const onDocDown = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) close();
        };
        document.addEventListener('mousedown', onDocDown);
        return () => document.removeEventListener('mousedown', onDocDown);
    }, [open, close]);

    useEffect(() => () => clearTimeout(timerRef.current), []);

    const runSearch = useCallback(async (text) => {
        const seq = ++requestSeq.current;
        setLoading(true);
        try {
            if (!sessionRef.current) sessionRef.current = await newSessionToken();
            const results = await fetchAddressSuggestions(text, {
                sessionToken: sessionRef.current,
                regionCodes,
            });
            if (seq !== requestSeq.current) return; // a newer keystroke won
            setSuggestions(results);
            setOpen(results.length > 0);
            setHighlight(-1);
        } catch (err) {
            // Quota, network or a misconfigured key — fall back to plain typing
            // rather than blocking the field.
            console.warn('[AddressAutocomplete] suggestions unavailable:', err?.message || err);
            if (seq === requestSeq.current) {
                setSuggestions([]);
                setOpen(false);
            }
        } finally {
            if (seq === requestSeq.current) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [regionKey]);

    const handleInput = (e) => {
        onChange?.(e);
        if (!isMapsConfigured() || applyingRef.current) return;

        const text = e.target.value;
        clearTimeout(timerRef.current);
        if ((text || '').trim().length < 3) {
            requestSeq.current++; // cancel any in-flight response
            setSuggestions([]);
            close();
            return;
        }
        timerRef.current = setTimeout(() => runSearch(text), DEBOUNCE_MS);
    };

    const choose = async (item) => {
        close();
        setLoading(true);
        try {
            const fields = await resolvePlace(item.prediction);
            applyingRef.current = true;
            onResolve?.(fields);
        } catch (err) {
            console.warn('[AddressAutocomplete] could not resolve place:', err?.message || err);
        } finally {
            applyingRef.current = false;
            setLoading(false);
            setSuggestions([]);
            // A session ends at selection — the next search starts a new one.
            sessionRef.current = null;
        }
    };

    const handleKeyDown = (e) => {
        if (!open || !suggestions.length) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((i) => (i + 1) % suggestions.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
        } else if (e.key === 'Enter') {
            if (highlight >= 0) {
                e.preventDefault();
                choose(suggestions[highlight]);
            }
        } else if (e.key === 'Escape') {
            close();
        }
    };

    const inline = variant === 'inline';

    const inlineInputClasses = 'px-3 py-2 bg-white dark:bg-[#3a4048] border border-gray-300 dark:border-gray-600 rounded-lg text-[13px] text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all w-full';

    return (
        <div ref={wrapRef} className={`relative ${inline ? 'flex flex-col' : ''} ${className}`}>
            {inline ? (
                <>
                    <label className="text-[11px] text-gray-500 dark:text-gray-400 font-medium mb-1.5">{label}</label>
                    <input
                        type="text"
                        name={name}
                        value={value || ''}
                        onChange={handleInput}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        className={inlineInputClasses}
                        autoComplete="off"
                    />
                </>
            ) : (
                <FormField
                    label={label}
                    name={name}
                    value={value || ''}
                    onChange={handleInput}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    required={required}
                    autoComplete="off"
                />
            )}

            {/* Status affordance — spinner while querying, pin otherwise. */}
            {isMapsConfigured() && (
                <span
                    className={`absolute right-3 text-gray-300 dark:text-gray-600 pointer-events-none ${inline ? 'top-[30px]' : 'top-1/2 -translate-y-1/2'}`}
                >
                    {loading ? <Loader2 size={15} className="animate-spin" /> : <MapPin size={15} />}
                </span>
            )}

            {open && suggestions.length > 0 && (
                <ul
                    role="listbox"
                    className="absolute z-50 left-0 right-0 top-full mt-1 max-h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#31373f] shadow-lg"
                >
                    {suggestions.map((item, i) => (
                        <li
                            key={item.id || i}
                            role="option"
                            aria-selected={i === highlight}
                            // mousedown (not click) so the input's blur doesn't
                            // close the list before the selection registers.
                            onMouseDown={(e) => { e.preventDefault(); choose(item); }}
                            onMouseEnter={() => setHighlight(i)}
                            className={`cursor-pointer px-3 py-2 text-[13px] ${
                                i === highlight
                                    ? 'bg-blue-50 dark:bg-blue-900/30'
                                    : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                            }`}
                        >
                            <div className="font-medium text-gray-800 dark:text-gray-200">{item.primary}</div>
                            {item.secondary && (
                                <div className="text-[11px] text-gray-500 dark:text-gray-400">{item.secondary}</div>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default AddressAutocomplete;
